/**
 * useAiStudio
 *
 * Orchestrates the five existing AI jobs (metadata, subtitles, dub, trailer,
 * scrubbing previews) as ONE user-facing run, so the operator picks what they want
 * once and then gets on with filling the form while results land.
 *
 * ── Design rules ─────────────────────────────────────────────────────────────
 * 1. It COMPOSES, it does not reimplement. Each task is driven by the same hook
 *    the per-section AI panels already use (`useAiVideoAnalysis`, `useAiSubtitles`,
 *    …). No service/network code is duplicated here, so a fix in a service is a
 *    fix in both paths.
 * 2. It owns NO form knowledge. Results are handed to `on<Task>` callbacks and the
 *    container decides what to do with them — same separation the individual hooks
 *    already keep.
 * 3. Nothing blocks. A run is fire-and-forget; the UI is free to unmount the
 *    launcher and show a small dock instead.
 * 4. Partial failure is normal, not fatal. One task 429-ing on Gemini quota must
 *    never take down the tasks that already succeeded.
 *
 * ── Scheduling ───────────────────────────────────────────────────────────────
 * Tasks are grouped into lanes (see `aiStudioTasks.js`). The "local" lane (ffmpeg
 * only, no AI quota) starts immediately. The "gemini" lane is throttled to
 * AI_STUDIO_GEMINI_CONCURRENCY at a time, because free-tier keys 429 if you fire
 * every job at once. Queued tasks are pumped as running ones finish.
 *
 * Status lifecycle per task:
 *   queued → running → done
 *                   ↳ failed  (retry via retryTask())
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAiVideoAnalysis from "./useAiVideoAnalysis";
import useAiSubtitles from "./useAiSubtitles";
import useAiDubbing from "./useAiDubbing";
import useAiTrailer from "./useAiTrailer";
import useAiThumbnails from "./useAiThumbnails";
import {
  AI_LANES,
  AI_STUDIO_GEMINI_CONCURRENCY,
  AI_STUDIO_TASKS,
  AI_STUDIO_TASK_MAP,
  AI_TASKS,
} from "../services/ai/aiStudioTasks";

export const TASK_STATE = {
  QUEUED: "queued",
  RUNNING: "running",
  DONE: "done",
  FAILED: "failed",
};

/** The Studio as a whole. `review` = finished, results waiting to be acknowledged. */
export const STUDIO_STATE = {
  IDLE: "idle",
  RUNNING: "running",
  REVIEW: "review",
};

export default function useAiStudio({
  video,
  onMetadata,
  onSubtitles,
  onDub,
  onTrailer,
  onThumbnails,
  geminiConcurrency = AI_STUDIO_GEMINI_CONCURRENCY,
} = {}) {
  // ── The five underlying job hooks ──
  // Each exposes the same { status, progress, activeStage, stages, result, error }
  // shape, which is what lets the scheduler below treat them uniformly.
  const metadataHook = useAiVideoAnalysis();
  const subtitlesHook = useAiSubtitles();
  const dubHook = useAiDubbing();
  const trailerHook = useAiTrailer();
  const thumbnailsHook = useAiThumbnails();

  const [studioState, setStudioState] = useState(STUDIO_STATE.IDLE);
  /** @type {[Record<string, {state:string, error?:string, startedAt?:number, finishedAt?:number}>, Function]} */
  const [taskStates, setTaskStates] = useState({});
  const [runConfig, setRunConfig] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Latest callbacks, so the scheduler effect doesn't need them as dependencies
  // (a parent re-render creating a new closure must not restart anything).
  const callbacksRef = useRef({});
  callbacksRef.current = { onMetadata, onSubtitles, onDub, onTrailer, onThumbnails };

  // Guards double-applying a result: a hook's `result` object identity is stable
  // per completed job, so remembering it is enough.
  const appliedRef = useRef({});
  // The config for the current run, readable from effects without re-subscribing.
  const configRef = useRef(null);
  // Set while cancel() is tearing a run down, so the scheduler stops pumping.
  const cancelledRef = useRef(false);

  // ── Task → hook adapter ──
  // The single place that knows how to start each job and which callback its
  // result belongs to. Everything else in this file is task-agnostic.
  const runners = useMemo(
    () => ({
      [AI_TASKS.METADATA]: {
        hook: metadataHook,
        start: (v) => metadataHook.analyze(v),
        apply: (result) => callbacksRef.current.onMetadata?.(result),
      },
      [AI_TASKS.SUBTITLES]: {
        hook: subtitlesHook,
        start: (v, cfg) => {
          const dubQueued = (cfg.tasks || []).includes(AI_TASKS.DUB);
          return subtitlesHook.generate(v, {
            targetLanguages: cfg.subtitleLanguages,
            // When a dub is part of the SAME run, this one transcription is made to
            // serve both jobs, turning two AI requests into one:
            //  - `diarize` adds the speaker labels the dub needs to cast voices
            //  - `transcribeLanguages` adds the dub's languages to the translation
            //    set, so there IS text for the dub to speak. Without it the shared
            //    transcript wouldn't cover the dub's languages and it would have to
            //    transcribe again — which is exactly the saving we're after.
            // Neither is requested otherwise: a leaner response is less likely to be
            // truncated, and unrequested translations would be wasted work.
            diarize: dubQueued,
            transcribeLanguages: dubQueued ? cfg.dubLanguages : [],
          });
        },
        apply: (result) => callbacksRef.current.onSubtitles?.(result),
      },
      [AI_TASKS.DUB]: {
        hook: dubHook,
        start: (v, cfg) =>
          dubHook.generate(v, {
            targetLanguages: cfg.dubLanguages,
            voice: cfg.dubVoice,
          }),
        apply: (result) => callbacksRef.current.onDub?.(result),
      },
      [AI_TASKS.TRAILER]: {
        hook: trailerHook,
        start: (v, cfg) =>
          trailerHook.generate(v, {
            durationTarget: cfg.trailerLength,
            title: cfg.title,
          }),
        apply: (result) => callbacksRef.current.onTrailer?.(result),
      },
      [AI_TASKS.THUMBNAILS]: {
        hook: thumbnailsHook,
        start: (v, cfg) => thumbnailsHook.generate(v, { interval: cfg.thumbInterval }),
        apply: (result) => callbacksRef.current.onThumbnails?.(result),
      },
    }),
    [metadataHook, subtitlesHook, dubHook, trailerHook, thumbnailsHook]
  );

  // ── Scheduler ────────────────────────────────────────────────────────────────
  // Runs on every render while a run is active. Two responsibilities:
  //   (a) settle tasks whose hook has reached a terminal status,
  //   (b) start queued tasks while the lane has capacity.
  // Both are expressed as a state patch so React batches them into one update.
  useEffect(() => {
    if (studioState !== STUDIO_STATE.RUNNING) return;
    if (cancelledRef.current) return;
    const cfg = configRef.current;
    if (!cfg) return;

    const patch = {};
    const toApply = []; // side effects deferred until after the state patch is built

    // (a) Settle anything that finished.
    Object.entries(taskStates).forEach(([taskId, ts]) => {
      if (ts.state !== TASK_STATE.RUNNING) return;
      const runner = runners[taskId];
      if (!runner) return;
      const { hook } = runner;

      if (hook.isComplete && hook.result) {
        patch[taskId] = { ...ts, state: TASK_STATE.DONE, finishedAt: Date.now() };
        if (appliedRef.current[taskId] !== hook.result) {
          appliedRef.current[taskId] = hook.result;
          toApply.push(() => runner.apply(hook.result));
        }
      } else if (hook.isFailed) {
        patch[taskId] = {
          ...ts,
          state: TASK_STATE.FAILED,
          finishedAt: Date.now(),
          error: hook.error || "This step could not be completed.",
        };
      }
    });

    // Merge the settle patch so capacity is computed against post-settle reality.
    const merged = { ...taskStates, ...patch };

    // (b) Fill free capacity. Local-lane tasks are unbounded (ffmpeg, no quota);
    // Gemini-lane tasks are capped so free-tier keys don't 429.
    const geminiRunning = Object.entries(merged).filter(
      ([id, ts]) =>
        ts.state === TASK_STATE.RUNNING &&
        AI_STUDIO_TASK_MAP[id]?.lane === AI_LANES.GEMINI
    ).length;

    let capacity = Math.max(0, geminiConcurrency - geminiRunning);

    // Preserve the registry order — cheap/fast tasks first, so the operator sees
    // something land early instead of waiting on the most expensive job.
    for (const task of AI_STUDIO_TASKS) {
      const ts = merged[task.id];
      if (!ts || ts.state !== TASK_STATE.QUEUED) continue;

      // Hold a task back until the tasks it depends on have SETTLED — done or failed.
      // Dubbing waits for subtitles so it can reuse that transcript instead of paying
      // for a second one. Gating on "settled" rather than "done" is deliberate: a
      // failed dependency must not strand the dependent task forever, it just means
      // the saving is missed. Dependencies not part of this run are ignored.
      const blocked = (task.dependsOn || []).some((depId) => {
        const dep = merged[depId];
        if (!dep) return false; // not in this run — nothing to wait for
        return dep.state !== TASK_STATE.DONE && dep.state !== TASK_STATE.FAILED;
      });
      if (blocked) continue;

      const isLocal = task.lane === AI_LANES.LOCAL;
      if (!isLocal && capacity <= 0) continue;
      if (!isLocal) capacity -= 1;

      patch[task.id] = { ...ts, state: TASK_STATE.RUNNING, startedAt: Date.now() };
      const runner = runners[task.id];
      // Starting a job is a side effect — defer it out of the render pass.
      if (runner) toApply.push(() => runner.start(video, cfg));
    }

    if (Object.keys(patch).length > 0) {
      setTaskStates((prev) => ({ ...prev, ...patch }));
    }
    toApply.forEach((fn) => fn());

    // Every task settled → the run is over.
    const allSettled = Object.values(merged).every(
      (ts) => ts.state === TASK_STATE.DONE || ts.state === TASK_STATE.FAILED
    );
    if (allSettled && Object.keys(merged).length > 0) {
      setStudioState(STUDIO_STATE.REVIEW);
    }
    // Intentionally re-evaluated on any hook status/progress change — those are the
    // only signals that can advance the schedule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    studioState,
    taskStates,
    video,
    geminiConcurrency,
    metadataHook.status,
    subtitlesHook.status,
    dubHook.status,
    trailerHook.status,
    thumbnailsHook.status,
  ]);

  // ── Elapsed timer (drives the dock's counter) ──
  useEffect(() => {
    if (studioState !== STUDIO_STATE.RUNNING || !startedAt) return;
    setElapsedMs(Date.now() - startedAt);
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
    return () => clearInterval(id);
  }, [studioState, startedAt]);

  /**
   * Start a run.
   * @param {object} config
   * @param {string[]} config.tasks              task ids to run
   * @param {string[]} [config.subtitleLanguages] extra translation targets
   * @param {string[]} [config.dubLanguages]      languages to dub into
   * @param {number}   [config.trailerLength]     seconds
   * @param {number}   [config.thumbInterval]     seconds between preview frames
   * @param {string}   [config.title]             used for trailer title cards
   */
  const run = useCallback(
    (config = {}) => {
      const ids = (config.tasks || []).filter((id) => AI_STUDIO_TASK_MAP[id]);
      if (ids.length === 0) return false;

      cancelledRef.current = false;
      appliedRef.current = {};

      const cfg = {
        subtitleLanguages: [],
        dubLanguages: [],
        trailerLength: 30,
        thumbInterval: 10,
        ...config,
        tasks: ids,
      };
      configRef.current = cfg;
      setRunConfig(cfg);

      const initial = {};
      ids.forEach((id) => {
        initial[id] = { state: TASK_STATE.QUEUED };
      });
      setTaskStates(initial);

      const now = Date.now();
      setStartedAt(now);
      setElapsedMs(0);
      setStudioState(STUDIO_STATE.RUNNING);
      return true;
    },
    []
  );

  /** Abort everything in flight and return to idle. */
  const cancel = useCallback(() => {
    cancelledRef.current = true;
    Object.values(runners).forEach(({ hook }) => hook.reset());
    appliedRef.current = {};
    configRef.current = null;
    setTaskStates({});
    setRunConfig(null);
    setStartedAt(null);
    setElapsedMs(0);
    setStudioState(STUDIO_STATE.IDLE);
  }, [runners]);

  /**
   * Re-queue one failed task. Used by the dock's per-row Retry — the common case
   * is a Gemini quota 429 that has since reset, where re-running everything would
   * waste the quota the successful tasks already spent.
   */
  const retryTask = useCallback((taskId) => {
    if (!AI_STUDIO_TASK_MAP[taskId]) return;
    const cfg = configRef.current;
    if (!cfg) return;
    cancelledRef.current = false;
    delete appliedRef.current[taskId];
    runners[taskId]?.hook.reset();
    setTaskStates((prev) => ({ ...prev, [taskId]: { state: TASK_STATE.QUEUED } }));
    setStudioState(STUDIO_STATE.RUNNING);
  }, [runners]);

  /** Dismiss the finished-run summary without touching the produced results. */
  const acknowledge = useCallback(() => {
    setStudioState(STUDIO_STATE.IDLE);
    setTaskStates({});
    setRunConfig(null);
    setStartedAt(null);
  }, []);

  // ── Derived view model for the UI ──
  // One flat array the dock can map over directly: registry metadata + live
  // progress + current stage label, per task.
  const tasks = useMemo(
    () =>
      AI_STUDIO_TASKS.filter((t) => taskStates[t.id]).map((task) => {
        const ts = taskStates[task.id];
        const { hook } = runners[task.id] || {};
        const stages = hook?.stages || [];
        const activeStage = hook?.activeStage;
        const stageLabel =
          activeStage === "complete"
            ? "Finishing up"
            : stages.find((s) => s.id === activeStage)?.label || null;

        // A settled task must read 100/0 regardless of where its hook's progress
        // happened to stop — the hook is shared and may be reset by a retry.
        const progress =
          ts.state === TASK_STATE.DONE
            ? 100
            : ts.state === TASK_STATE.RUNNING
            ? hook?.progress || 0
            : 0;

        return {
          ...task,
          state: ts.state,
          progress,
          stageLabel,
          stages,
          activeStage,
          error: ts.error,
          startedAt: ts.startedAt,
          finishedAt: ts.finishedAt,
          result: ts.state === TASK_STATE.DONE ? hook?.result : null,
        };
      }),
    [taskStates, runners]
  );

  const counts = useMemo(() => {
    const c = { total: tasks.length, done: 0, failed: 0, running: 0, queued: 0 };
    tasks.forEach((t) => {
      if (t.state === TASK_STATE.DONE) c.done += 1;
      else if (t.state === TASK_STATE.FAILED) c.failed += 1;
      else if (t.state === TASK_STATE.RUNNING) c.running += 1;
      else c.queued += 1;
    });
    return c;
  }, [tasks]);

  // Overall progress: each task contributes an equal share of its own progress, so
  // the bar advances smoothly instead of jumping per completed task.
  const overallProgress = useMemo(() => {
    if (tasks.length === 0) return 0;
    const sum = tasks.reduce((acc, t) => {
      if (t.state === TASK_STATE.DONE || t.state === TASK_STATE.FAILED) return acc + 100;
      return acc + (t.progress || 0);
    }, 0);
    return Math.min(100, Math.round(sum / tasks.length));
  }, [tasks]);

  return {
    studioState,
    isIdle: studioState === STUDIO_STATE.IDLE,
    isRunning: studioState === STUDIO_STATE.RUNNING,
    isReview: studioState === STUDIO_STATE.REVIEW,
    tasks,
    counts,
    overallProgress,
    elapsedMs,
    runConfig,
    run,
    cancel,
    retryTask,
    acknowledge,
  };
}
